import type { ResponseResult } from '@/types/services/common'

/** One pending AI suggestion from the read endpoint. */
export interface AiSuggestion {
  id: string
  // Exactly one is set; ICOs are keyed by ico_id. Prefer `assetKeyOf()`.
  currency_id: string | null
  ico_id: string | null
  symbol: string
  name: string
  // Decimals serialized as strings by the backend.
  value_usd: string
  suggested_allocation: string | null
  suggested_segment_tags: string[]
  // Single category-tag name (closed vocabulary); null = no category.
  suggested_category: string | null
  reasoning: string
  confidence: string
  status: string
  create_date: string
  // From an explicit re-suggest: confirming *replaces* the current tags, so
  // every axis is a live decision and the row is always shown.
  replaces_existing: boolean
}

/** The asset's current values, per AI axis, resolved live on the FE. */
export interface CurrentAxisValues {
  segment_tags: string[]
  category: string | null
  // Allocation tag name; null = unallocated ("Other"), undefined = unknown
  // (the asset's tags are not readable yet).
  allocation?: string | null
}

/** One item of the bulk-apply request. */
export interface AiSuggestionApplyItem {
  id: string
  status: 'confirmed'
  // Names, not UUIDs. Presence decides for allocation: omitted =
  // untouched, explicit null = unallocated; omitted segments = keep
  // suggested.
  allocation?: string | null
  segment_tags?: string[]
  // Category name; explicit null = "no category" (deliberate decline),
  // omitted = keep the AI suggestion.
  category?: string | null
}

export interface AiSuggestionsResponse {
  result: ResponseResult
  data: AiSuggestion[]
}

export interface AiSuggestionApplyResult {
  confirmed: number
}

export interface AiSuggestionApplyResponse {
  result: ResponseResult
  data: AiSuggestionApplyResult
}

export interface AiResuggestResult {
  queued: number
  skipped: number
}

export interface AiResuggestResponse {
  result: ResponseResult
  data: AiResuggestResult
}

/** An entity with suggestions waiting for review. */
export interface AiSuggestionsPendingEntity {
  entity_id: string
  // Raw count: includes suggestions that match the current classification.
  pending_count: number
}

/**
 * A suggestion's asset id: currency id, or ICO id for pre-launch positions.
 * The same key `assetInfoMap` and `getCurrencyId()` already use.
 */
export function assetKeyOf(suggestion: AiSuggestion): string {
  return suggestion.currency_id ?? suggestion.ico_id ?? ''
}
